import React, { useEffect, useState } from 'react';
import PapaParse from 'papaparse';
import PropTypes from 'prop-types';
import { Button, Drawer } from '@itds/elements/index.js';

import { Api } from '@brokerage/utils/Api';
import { Alert, AttributeTable, Field, FileField } from '@itds/elements';
import { Colors, FontSizes } from '@itds/Theme';
import { useParams } from 'react-router-dom';

const ImportMembers = ({ onClose, show }) => {
  const params = useParams();

  const styles = {
    content: {
      maxWidth: 600,
      maxHeight: 'calc(100vh - 230px)'
    },
    description: {
      lineHeight: '1.4em',
      fontSize: FontSizes.LARGE,
      textAlign: 'center'
    }
  };

  useEffect(() => {
    return () => {
      setData({
        import_csv_file: null,
        file_parse_errors: [],
        file_parse_started: false,
        started: false,
        error: false
      });
    };
  }, [onClose]);

  const [data, setData] = useState({
    import_csv_file: null,
    file_parse_errors: [],
    file_parse_started: false,
    started: false,
    error: false
  });

  const import_fields = {
    email:
      'This must be unique and is the only field that is required. If the email is already associated with a Member in your Agency, then this row will be ignored.',
    first_name: 'The first name of the member.',
    middle_name: 'The middle name of the member.',
    last_name: 'The last name of the member.',
    phone: 'The US phone number for the member. The preferred format is (555) 555-5555.',
    gender: 'If providing this value, it must be one of the following: "Male", "Female".',
    street: 'The street address for the member.',
    street2: 'The second line street address for the member.',
    city: 'The US city for the member.',
    state: 'The US two character state code for the member.',
    zip_code: 'The US zip code for the member.',
    image_url: "This is a url to a .png image. This will be used as the member's profile picture.",
    role: 'This is the role of the member within your Agency. It must be one of the following: "member", "sales_rep", "admin". If no role is provided then it will be set to "member".',
    insurance_license_number: 'The life insurance license number for the member.',
    insurance_license_state:
      "The US two character state code associated with the member's life insurance license",
    protective_contract_number:
      'The member contract number for Protective Life for your Agency. This will be used when submitted E-Tickets directly to Protective Life.'
  };

  const handleCsvChange = file => {
    setData({
      import_csv_file: file
    });
  };

  const handleImportMembers = () => {
    const agency_id = parseInt(params.agency_id, 10);

    if (data.import_csv_file) {
      PapaParse.parse(data.import_csv_file, {
        header: true,
        complete: results => {
          if (results.errors.length) {
            setData({
              file_parse_errors: results.errors,
              file_parse_started: false
            });
          } else {
            setData({
              file_parse_errors: [],
              file_parse_started: true
            });

            Api.importMembers(agency_id, results.data)
              .then(({}) => {
                setData({ started: true, errors: false });
              })
              .catch(() => {
                setData({ started: false, error: true });
              });
          }
        }
      });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={!data.started ? <Button onClick={handleImportMembers}>Import CSV</Button> : null}
      onClose={onClose}
      show={show}
      title='Import Members'
    >
      {data.started ? (
        <div style={styles.description}>
          <i className='mdi mdi-check-bold' style={{ color: Colors.GREEN.hex, fontSize: 60 }} />
          <div>
            Your member import has started and will run in the background. It could take a few
            minutes so we'll email you once it's done.
          </div>
        </div>
      ) : (
        <div style={styles.content}>
          {data.file_parse_errors &&
            data.file_parse_errors.map(error => <Alert type='error'>{error.message}</Alert>)}

          {data.error ? (
            <Alert type='error'>
              An error occured while importing. Check the .csv and make sure it is properly
              formatted.
            </Alert>
          ) : null}

          <div style={styles.description}>
            Upload a .csv file that contains all of your members' information. The .csv must contain
            the headers listed below. Headers that are not listed below will be ignored.
          </div>
          <AttributeTable
            data={import_fields}
            preserveKeyFormat={true}
            style={{ margin: '30px 0', maxHeight: 430, overflow: 'auto' }}
          />
          <Field label='Select .csv file to import'>
            <FileField file={data.import_csv_file} onChange={handleCsvChange.bind(null)} />
          </Field>
        </div>
      )}
    </Drawer>
  );
};

ImportMembers.propTypes = {
  members: PropTypes.array,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  show: PropTypes.bool
};

export default ImportMembers;
